import type React from 'react';

import { Alert, Button, Modal, ModalBody, ModalFooter, ModalHeader, Text } from '@change/ds-core-ui';

import { Loader } from '@change-corgi/design-system-legacy/components/progressiveDisclosure';

import type { MediaHitEmbed } from '../../../shared/context/types';
import { useDeleteMediaLogic } from '../hooks/useDeleteMediaLogic';

type DeleteMediaModalProps = {
	isOpen: boolean;
	onOpenChange: (open: boolean) => void;
	media: MediaHitEmbed;
	petitionId: string;
	onResult: (result: { value: 'success' | 'error' | undefined }) => void;
};

export const DeleteMediaModal: React.FC<DeleteMediaModalProps> = ({
	isOpen,
	onOpenChange,
	media,
	petitionId,
	onResult,
}) => {
	const { isDeleteLoading, deleteStatus, handleCancel, handleDelete } = useDeleteMediaLogic({
		media,
		petitionId,
		onResult,
		onOpenChange,
	});

	return (
		<Modal isOpen={isOpen} onOpenChange={onOpenChange} isDismissable={false} isKeyboardDismissDisabled>
			<ModalHeader title="Delete Media Hit" />
			<ModalBody>
				<div data-testid="delete-confirmation" className="flex flex-col">
					<Text variant="body" className="mb-8">
						Are you sure you want to permanently delete this media hit from {media.url}?
					</Text>
					<div className="flex justify-center">{isDeleteLoading && <Loader mb={16} data-testid="delete-loader" />}</div>
					{deleteStatus === 'success' && (
						<Alert variant="success" heading="Success" className="mb-2">
							Media hit successfully removed.
						</Alert>
					)}
					{deleteStatus === 'error' && (
						<Alert variant="error" heading="Delete failed" className="mb-2">
							Something went wrong while deleting.
						</Alert>
					)}
				</div>
			</ModalBody>
			{deleteStatus !== 'success' && (
				<ModalFooter>
					{() => (
						<>
							<Button
								variant="primary"
								data-testid="confirm-delete-btn"
								data-qa="confirm-delete-btn"
								onClick={handleDelete}
								disabled={isDeleteLoading}
								className="uppercase"
							>
								Permanently Remove
							</Button>
							<Button
								variant="secondary"
								data-testid="confirm-cancel-btn"
								data-qa="confirm-cancel-btn"
								onClick={handleCancel}
								disabled={isDeleteLoading}
							>
								Cancel
							</Button>
						</>
					)}
				</ModalFooter>
			)}
		</Modal>
	);
};
